export const post = {
    id: "tired-but-chosen",
    title: "Tired, But It's My Choice",
    excerpt:
        "The exhaustion I feel now is mine by choice—a weight I carry to build a life I want, not the crushing load of a past I didn't choose.",
    image: "/images/blogs/tired-but-chosen.jpg",
    category: ["Personal Growth"],
    date: "August 26, 2025",
    readTime: 6,
    slug: "tired-but-chosen",
    content: `
I'm tired. Bone-deep tired. It's past midnight, and I've been staring at code for hours trying to fix a bug for a client that shouldn't even exist. Tomorrow I'll wake up at 5 AM to do my long run when my legs still ache from yesterday.

But the thing is, this tired is different. It's not the same as before. It isn't the weight of a world I didn't choose crushing me. This is the weight I picked up. The work I chose to do.

## The Me in the Dark

I remember what the other kind of tired felt like. The kind that hollows you out. The kind where you're exhausted from doing nothing, from the weight of rejections and uncertainty and wondering if you'll ever climb out.

Just like me a year ago. Eight months jobless after uni. No clear path forward. Just applications sent to some random bot and silence. So much silence. I'd wake up tired. Go to bed tired. Tired from carrying doubt, from feeling left behind while everyone else moved on.

Back then I didn't choose my burdens. They were dumped on me. Every rejection felt like proof I wasn't enough. Every day without progress felt like failure. I kept going because I had to, not because I wanted to. That version of me didn't break, somehow. He's the reason I'm here now.

## The Weight I Chose

These days I'm still tired, but I chose this. The late nights debugging because I want to produce something of value, something that lasts. The 5 AM runs because I need to prove to myself that I'm not the man I was. The hours spent learning, building, pushing when I'd rather scroll or sleep or just... stop.

Some nights I collapse into bed, body aching, mind still racing, and I wonder if I'm pushing too hard. The voice in my head whispers: *You could stop. You could rest. You could let this go.*

And yeah, I could. I could sleep in tomorrow. Skip the run. Half-ass the work. Plenty of people do. But I know where that leads. I've seen what happens when you let the weight win. It's not rest—it's surrender. It's becoming the version of yourself you said you'd never be.

I didn't fight through hell just to betray the person who got me here.

## When I'm at My Limit

When I feel like I'm at my limit, I think about him—the me in the dark. There's something ironic about my name. Alwi means "to rise." Back then, rising felt impossible. I was down, buried in doubt, with no guarantee I'd make it out.

But he did. He had nothing but stubbornness and a refusal to quit. No safety net. No clear path. Just the will to keep moving.

If he could rise from that with no hope in sight, who am I to complain about burdens I chose? This tiredness is proof I'm alive. Proof I'm still fighting. It's the kind of tired that builds, not breaks.

## When It's Not Easy

Some days I cry. Not because I'm weak, but because I'm human. Carrying what you choose doesn't mean it's light—it just means it's worth it. Those tears are iron, forged in the same fire that made me. They're a reminder of the Alwi who didn't quit, who handed me the torch and said, "Keep going." I owe him. Every run, every project, every time I get up when I want to stay down—it's for him.

Carrying what you choose doesn't mean it's light. It just means it's yours. And on the days when I want to quit, I remind myself: I owe it to the version of me who didn't have a choice. Who carried the weight of things he didn't pick, who survived when survival felt impossible.

Every run, every line of code, every time I get up when I want to stay down—it's for him. Because he earned this chance for me.

## What I'm Really Carrying

Freedom isn't about having no burdens. It's about choosing which ones you'll carry. I could let go. Slip into ease. Stop pushing so hard. A lot of people do.

But that's not me. I choose this weight because... honestly? Because I don't know who I'd be without it. This is shaping me into someone I can respect. When I lie down at night, tired as hell, I can be proud that this tired is mine.

Most days I believe it. Some days I don't. But I keep going anyway.

## For Anyone Else in the Fight

If you're carrying your own weight, chosen or notm, you're not alone. It's okay to feel the strain. It's okay to stumble. It's okay to cry and doubt and wonder if you're doing this right.

But don't let go of what you chose. You're tired because you're fighting for something real. That matters, even when it doesn't feel like it.

And to myself, on the days I read this back: don't betray the me in the dark. He carried you here through hell. Your name means to rise—so rise. Even when you're tired. Even when you're not sure. This tired is nothing compared to below.

Keep going. One more step.

I'll sleep tired tonight. But it's my tired, and I wouldn't trade it for anything.
`,
};
